"use client";

import { c as _c } from "react/compiler-runtime";
import { CSSProperties, useLayoutEffect, useRef, useState } from "react";
import { Container, CaseStudyCard, ScrollStack } from "@/components";
import { useHeadingRail } from "@/hooks/animations/useHeadingRail";
import CaseStudyHeadingRail from "./CaseStudyHeadingRail";
import { caseStudyData } from "@/data/CaseStudyData";

// Gap between the heading block and where the deck's flow begins.
const CARD_GAP = 12;
const BAND_HEIGHT = 64;
// Trailing runway after the last card pins — folded into the lg deck trim, so a
// bigger value pushes the whole stack further off the section's bottom.
const DECK_PB = 140;
// Row gap between cards as they flow in, before each pins. Kept equal to
// DECK_TOP (24) so the space a pinned card leaves at the top matches the space
// between cards. Purely pre-pin breathing room — pinned cards stack by
// BAND_HEIGHT regardless.
const DECK_CARD_GAP = 24;
const TRIM_EXTRA = (caseStudyData.length - 1) * BAND_HEIGHT + DECK_PB + CARD_GAP;

// How far the pinned deck sits from the top of the viewport.
//
// This used to be the whole height of the heading, because the heading pinned
// horizontally above the cards — which pushed the last card's bottom past 900px
// and took every card's "View Case Study" link off the bottom of a laptop
// screen. With the heading turned aside there is nothing above the deck left to
// clear, so the cards pin near the top and the link stays in view.
const DECK_TOP = 24;
const CaseStudy = () => {
  const $ = _c(25);
  const headingRef = useRef(null);
  const triggerRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      w: 0,
      h: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [headingBox, setHeadingBox] = useState(t0);
  const cardRef = useRef(null);
  const [cardH, setCardH] = useState(0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const heading = headingRef.current;
      const card = cardRef.current;
      const observers = [];
      if (heading) {
        const update = () => setHeadingBox({
          w: heading.offsetWidth,
          h: heading.offsetHeight
        });
        update();
        const ro = new ResizeObserver(update);
        ro.observe(heading);
        observers.push(ro);
      }
      if (card) {
        const update_0 = () => setCardH(card.offsetHeight);
        update_0();
        const ro_0 = new ResizeObserver(update_0);
        ro_0.observe(card);
        observers.push(ro_0);
      }
      return () => observers.forEach(_temp);
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useLayoutEffect(t1, t2);
  const {
    isDesktop,
    topOpacity,
    topY,
    railWidth,
    railReveal,
    railY
  } = useHeadingRail(triggerRef, headingBox.w, headingBox.h);
  const deckTrim = cardH + TRIM_EXTRA;
  const t3 = `${deckTrim}px`;
  let t4;
  if ($[3] !== t3) {
    t4 = {
      "--deck-trim": t3
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = t4 as CSSProperties;
  let t6;
  if ($[5] !== headingBox.h || $[6] !== isDesktop || $[7] !== railReveal || $[8] !== railWidth || $[9] !== railY || $[10] !== topOpacity || $[11] !== topY) {
    t6 = <CaseStudyHeadingRail innerRef={headingRef} triggerRef={triggerRef} headingHeight={headingBox.h} isDesktop={isDesktop} topOpacity={topOpacity} topY={topY} railWidth={railWidth} railReveal={railReveal} railY={railY} />;
    $[5] = headingBox.h;
    $[6] = isDesktop;
    $[7] = railReveal;
    $[8] = railWidth;
    $[9] = railY;
    $[10] = topOpacity;
    $[11] = topY;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `${headingBox.h + CARD_GAP}px`;
  let t8;
  if ($[13] !== t7) {
    t8 = {
      paddingTop: t7
    };
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ScrollStack topOffset={`${DECK_TOP}px`} bandHeight={BAND_HEIGHT} scaleStep={0} itemGap={DECK_CARD_GAP} endTrim="calc(-1 * var(--deck-trim))">{caseStudyData.map((caseStudy, i) => <div key={caseStudy?.id} ref={i === 0 ? cardRef : undefined} className="w-full"><CaseStudyCard data={caseStudy} /></div>)}</ScrollStack>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8) {
    t10 = <div className="hidden lg:block lg:min-w-0 lg:flex-1" style={t8}>{t9}</div>;
    $[16] = t8;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex flex-col gap-6 lg:hidden w-full">{caseStudyData.map(_temp2)}</div>;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t6) {
    t12 = <Container className="px-4 lg:px-10 flex flex-col lg:flex-row h-full justify-center">{t6}{t10}{t11}</Container>;
    $[19] = t10;
    $[20] = t6;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t5) {
    t13 = <section id="projects" style={t5} className="relative w-full flex justify-center bg-accent-foreground pt-16 pb-12 md:pt-20 md:pb-8 lg:pt-28 lg:pb-(--deck-trim)">{t12}</section>;
    $[22] = t12;
    $[23] = t5;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
};
export default CaseStudy;
function _temp(ro_1) {
  return ro_1.disconnect();
}
function _temp2(caseStudy_0) {
  return <div key={caseStudy_0?.id} className="w-full"><CaseStudyCard data={caseStudy_0} /></div>;
}